import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Image, KeyValueDTO } from '@ms/core';
import { Pageable } from '@ms/core/components/table/pageable.model';
import { httpParams, logRequest } from 'app/main/shared/http-utils';
import { HTTP_CLIENT_XHR } from 'app/system/http-client-provider';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlphabeticalStudentList } from '../education/reports/alphabetical-book-of-students/alphabetical-book-of-students.model';
import { BookOfTheAchievments } from '../education/reports/book-of-the-achievements/book-of-the-achievements.model';
import { SocialPassportOfSchool } from '../education/reports/social-passport-of-school/social-passport-of-school.model';
import { StudentInfoReport } from '../education/reports/student-info-report/student-info-report.model';
import { UserAttachment } from '../shared/attachments/user-attachment.model';
import { IdCardSearch } from '../shared/model/id-card-search';
import { Dictionary } from '../shared/model/shared.model';
import { StudentAutocompleteSearchDTO } from '../shared/model/student-autocomplete.model';
import { StudentCourse } from '../shared/model/student-course.model';
import { StudentRepresentative } from '../shared/model/student-representative.model';
import { UserAccountCreateDTO } from '../shared/model/user-account-create.model';
import { IPage } from '../shared/page/page.model';
import { StudentTextDTO } from '../student-list/students-upload-dialog/student-import.model';
import {
    GPACalculationType,
    GPACalculationTypes,
    TranscriptType,
    TranscriptTypes,
} from '../student-transcripts/student-transcript.model';
import { EnrollmentInfo, IStudentReportHeader, StudentIdGenerateDTO, StudentList } from '../students';
import { StudentEnroll } from '../students/model/student-enroll';
import { CourseStudentSearch } from './../shared/model/course.model';
import { Alert, IStudent, Student, StudentProfileInfo } from './../students/model/student.model';
import {
    IPageableStudent,
    IStudentSearch,
    StudentEnrollmentSearch,
} from './../students/student-list/students-search.model';
import { AttachmentService } from './attachment.service';
import { SchoolService } from './school.service';
@Injectable({
    providedIn: 'root',
})
export class StudentService implements AttachmentService {
    baseUrl = '/school-service/api/students';
    schoolBaseUrl = '/school-service/api/classes';

    xhrHttp = inject(HTTP_CLIENT_XHR);
    constructor(
        private http: HttpClient,
        private schoolService: SchoolService
    ) {}

    downloadFileByUrl(attachmentUrl: string) {
        return this.http.get(`${this.baseUrl}/attachmentUrl/${attachmentUrl}?time=${new Date().getTime()}`, {
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getStudents(schoolId: string): Observable<IStudent[]> {
        return this.http.get<IStudent[]>(`${this.baseUrl}/${schoolId}/school`);
    }

    getStudentById(id: string): Observable<HttpResponse<IStudent>> {
        return this.http.get<IStudent>(`${this.baseUrl}/${id}`, {
            observe: 'response',
        });
    }

    getStudentCourses(id: string): Observable<HttpResponse<StudentCourse[]>> {
        return this.http.get<StudentCourse[]>(`${this.baseUrl}/courses/student/${id}`, {
            observe: 'response',
        });
    }

    getStudentReportHeaderById(id: string): Observable<HttpResponse<IStudentReportHeader>> {
        return this.http.get<IStudentReportHeader>(`${this.baseUrl}/report-header/${id}`, {
            observe: 'response',
        });
    }

    getStudentByUserId(userId: string, schoolId: string): Observable<HttpResponse<IStudent>> {
        return this.http.get<IStudent>(`${this.baseUrl}/user/${userId}/school/${schoolId}`, {
            observe: 'response',
        });
    }

    getStudentByUserIds(userIds: string[], schoolId: string): Observable<HttpResponse<IStudent[]>> {
        return this.http.post<IStudent[]>(`${this.baseUrl}/school/${schoolId}`, userIds, { observe: 'response' });
    }

    getStudentsByGradeLevelAndAutocomplete(
        enrolledClassId: string,
        gradeLevelId: string,
        text: string,
        showInActiveStudent: boolean,
        courseIds?: string[]
    ): Observable<HttpResponse<KeyValueDTO<Dictionary<any>>[]>> {
        const searchDTO = new StudentAutocompleteSearchDTO(
            enrolledClassId,
            gradeLevelId,
            text,
            this.schoolService.getActiveSchool().id,
            courseIds,
            showInActiveStudent
        );
        console.log('AUTO-COMPLETE', searchDTO);
        return this.http
            .post<KeyValueDTO<Dictionary<any>[]>>(`${this.baseUrl}/autocomplete`, searchDTO, { observe: 'response' })
            .pipe(
                map(results => results),
                catchError(() => [])
            );
    }

    createStudent(student: Student): Observable<HttpResponse<IStudent>> {
        student.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IStudent>(this.baseUrl, student, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateStudent(student: Student): Observable<HttpResponse<IStudent>> {
        student.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IStudent>(this.baseUrl, student, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateAlerts(studentId: string, alerts: Dictionary<Alert>) {
        return this.http.put<IStudent>(`${this.baseUrl}/update-alerts/${studentId}`, alerts, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    enrollStudent(studentId: string, enrollDto: StudentEnroll): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.baseUrl}/${studentId}/enroll`, enrollDto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    enrollStudentList(studentList: StudentList): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.baseUrl}/enroll-list`, studentList, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    deleteStudent(id: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${id}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    deleteStudentForce(id: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${id}/force`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getImage(id: string, format: 'url' | 'base64' = 'url'): Observable<Image> {
        return this.http.get<Image>(`${this.baseUrl}/${id}/image/${format}`);
    }

    connected(id: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.baseUrl}/connection-check/${id}`);
    }

    deleteImage(studentId: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${studentId}/image`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    mapRepresentatives(response: StudentRepresentative): StudentRepresentative {
        const result: StudentRepresentative = Object.assign({}, response);
        result.profile = Object.assign({}, result.profile);
        result.profile.address = Object.assign({}, result.profile.address);
        result.profile.address.country = Object.assign({ id: null }, result.profile.address.country);
        result.profile.documentInfo = Object.assign({}, result.profile.documentInfo);
        result.profile.contactInfo = Object.assign({}, result.profile.contactInfo);
        return result;
    }

    map(response: IStudent): IStudent {
        const result: IStudent = Object.assign({}, response);
        result.profile = Object.assign({}, result.profile);
        result.enrolledClass = Object.assign({ id: null }, result.enrolledClass);

        result.department = Object.assign({ id: null }, result.department);
        result.attachments = Object.assign([{}], result.attachments);
        result.profile.address = Object.assign({}, result.profile.address);
        result.currentGradeLevel = Object.assign({}, result.currentGradeLevel);
        result.profile.address.state = Object.assign({ id: null }, result.profile.address.state);
        result.profile.address.country = Object.assign({ id: null }, result.profile.address.country);
        result.profile.documentInfo = Object.assign({}, result.profile.documentInfo);
        result.profile.contactInfo = Object.assign({}, result.profile.contactInfo);
        if (result?.representatives?.length > 0) {
            for (let i = 0; i < result.representatives.length; i++) {
                result.representatives[i] = this.mapRepresentatives(result.representatives[i]);
            }
        }

        return result;
    }

    searchStudentsWithPagination(
        studentSearchDTO: IStudentSearch,
        params?: Pageable
    ): Observable<HttpResponse<IPage<IPageableStudent>>> {
        studentSearchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IPage<IPageableStudent>>(`${this.baseUrl}/search/pageable`, studentSearchDTO, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    searchStudentsWithParentsPagination(
        studentSearchDTO: IStudentSearch,
        params?: Pageable
    ): Observable<HttpResponse<IPage<IPageableStudent>>> {
        studentSearchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IPage<IPageableStudent>>(`${this.baseUrl}/parents/search/pageable`, studentSearchDTO, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    uploadFile(entityId: string, id: string, formData: FormData): Observable<HttpResponse<UserAttachment>> {
        return this.http.post<UserAttachment>(`${this.baseUrl}/${entityId}/attachment/${id}`, formData, {
            observe: 'response',
        });
    }

    updateImage64(id: string, image64: string): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/image64/${id}`, image64, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    uploadPhotos(schoolId: string, formData: FormData) {
        return this.xhrHttp.post<any>(`${this.baseUrl}/upload/photos/${schoolId}`, formData, {
            observe: 'response',
            reportProgress: true,
        });
    }

    downloadFile(entityId: string, id: string) {
        return this.http.get(`${this.baseUrl}/${entityId}/attachment/${id}`, {
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    deleteFile(entityId: string, id: string, attachmentUrl: string) {
        return this.http.delete(`${this.baseUrl}/${entityId}/attachment/${id}/url/${attachmentUrl}`);
    }

    getAttachments(entityId: string): Observable<UserAttachment[]> {
        return this.http.get<UserAttachment[]>(`${this.baseUrl}/${entityId}/attachment`);
    }

    getByAutocomplete(
        enrolledClassId: string,
        gradeLevelId: string,
        searchString: string,
        showInActiveStudent: boolean
    ): Observable<HttpResponse<KeyValueDTO<Dictionary<any>>[]>> {
        return this.getStudentsByGradeLevelAndAutocomplete(
            enrolledClassId,
            gradeLevelId,
            searchString,
            showInActiveStudent
        );
    }

    exportXLS(searchDTO: IStudentSearch): Observable<HttpResponse<any>> {
        searchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post(`${this.baseUrl}/export/xls`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    createStudentUsers(studentIDs: string[]): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/user`, studentIDs, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    createStudentUser(request: UserAccountCreateDTO): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/user-account`, request, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    createParentUser(request: StudentRepresentative, studentId: string): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/${studentId}/parent/user-account`, request, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    downloadTemplate(): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get(`${this.baseUrl}/school/${schoolId}/download-template`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getStudentsFromExcel(formData: FormData, schoolId: string): Observable<HttpResponse<StudentTextDTO[]>> {
        return this.xhrHttp.post<StudentTextDTO[]>(`${this.baseUrl}/upload-excel/${schoolId}`, formData, {
            observe: 'response',
            reportProgress: true,
        });
    }

    import(
        list: StudentTextDTO[],
        enroll: StudentEnroll,
        existingFields: string[],
        classId: string
    ): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<any>(
            `${this.baseUrl}/class/${classId}/school/${schoolId}/import`,
            { list, enroll, existingFields },
            {
                observe: 'response',
                headers: logRequest(),
            }
        );
    }

    getStudentsByCoursePageable(
        courseId: string,
        academicYearId: string,
        params?: Pageable
    ): Observable<HttpResponse<IPage<IPageableStudent>>> {
        return this.http.get<IPage<IPageableStudent>>(
            `${this.baseUrl}/course/${courseId}/academic-year/${academicYearId}/search`,
            {
                observe: 'response',
                params: httpParams(params),
            }
        );
    }

    getStudentsByCourse(courseId: string): Observable<HttpResponse<IPageableStudent[]>> {
        return this.http.get<IPageableStudent[]>(`${this.baseUrl}/course/${courseId}`, {
            observe: 'response',
        });
    }

    getStudentsByCourseExceptGroupStudents(
        courseId: string,
        academicYearId: string,
        groupStudents: string[],
        params?: Pageable
    ): Observable<HttpResponse<IPage<IPageableStudent>>> {
        const searchDTO = new CourseStudentSearch();
        searchDTO.courseId = courseId;
        searchDTO.academicYearId = academicYearId;
        searchDTO.groupStudents = groupStudents;

        return this.http.post<IPage<IPageableStudent>>(`${this.baseUrl}/search-for-group`, searchDTO, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    getStudentsByGroupPageable(groupId: string, pageable: Pageable): Observable<HttpResponse<IPage<IPageableStudent>>> {
        return this.http.get<IPage<IPageableStudent>>(`${this.baseUrl}/group/${groupId}`, {
            observe: 'response',
            params: httpParams(pageable),
        });
    }

    getStudentsByIds(studentIds: string[], params?: Pageable): Observable<HttpResponse<IPage<IPageableStudent>>> {
        return this.http.post<IPage<IPageableStudent>>(`${this.baseUrl}/course/search-by-ids`, studentIds, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    getStudentsWithIds(studentIds: string[]): Observable<HttpResponse<IStudent[]>> {
        return this.http.post<IStudent[]>(`${this.baseUrl}/search-by-ids`, studentIds, {
            observe: 'response',
        });
    }

    deleteStudentsFromCourse(
        students: string[],
        academicYearId: string,
        courseId: string,
        transfer?: boolean,
        transferTermGrades?: boolean,
        transferCurrentProgress?: boolean,
        targetCourseId?: string,
        enrollAssignments?: boolean
    ): Observable<HttpResponse<any>> {
        return this.http.post(
            `${this.baseUrl}/remove-students-from-course`,
            {
                students,
                academicYearId,
                courseId,
                transfer,
                transferTermGrades,
                transferCurrentProgress,
                targetCourseId,
                enrollAssignments,
            },
            {
                observe: 'response',
                headers: logRequest(),
            }
        );
    }
    generateStudentCard(searchDTO: IdCardSearch): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/generate-card`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getStudentProfileInfo(schoolId: string, studentId?: string): Observable<HttpResponse<StudentProfileInfo>> {
        return this.http.get<StudentProfileInfo>(`${this.baseUrl}/profile-info/${studentId}/school/${schoolId}`, {
            observe: 'response',
        });
    }

    searchClassStudentsWithoutPagination(
        studentSearchDTO: IStudentSearch
    ): Observable<HttpResponse<IPageableStudent[]>> {
        studentSearchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IPageableStudent[]>(`${this.baseUrl}/search-by-class`, studentSearchDTO, {
            observe: 'response',
        });
    }

    searchStudentsWithinClassWithoutPagination(
        studentSearchDTO: IStudentSearch
    ): Observable<HttpResponse<IPageableStudent[]>> {
        studentSearchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IPageableStudent[]>(`${this.schoolBaseUrl}/search-students`, studentSearchDTO, {
            observe: 'response',
        });
    }

    getStudentIdsMap(studentUserIds: string[], schoolId: string): Observable<HttpResponse<{ [key: string]: string }>> {
        return this.http.post<{ [key: string]: string }>(
            `${this.baseUrl}/search/user-ids/school/${schoolId}`,
            studentUserIds,
            {
                observe: 'response',
            }
        );
    }

    bulkImportPreRegStudents(studentIds: string[]): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.baseUrl}/bulk-import-students`, studentIds, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    exportStudentsXLS(studentSearchDTO: IStudentSearch): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/export/class/xls`, studentSearchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    updateRepresentatives(
        studentRepresentatives: StudentRepresentative[],
        studentId: string
    ): Observable<HttpResponse<StudentRepresentative[]>> {
        return this.http.post<StudentRepresentative[]>(
            `${this.baseUrl}/${studentId}/update-representatives`,
            studentRepresentatives,
            {
                observe: 'response',
                headers: logRequest(),
            }
        );
    }

    bulkCreateRepresentativeAccount(list: string[]): Observable<HttpResponse<void>> {
        return this.http.post<void>(`${this.baseUrl}/representatives/create-account-bulk`, list, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getStudentSpecialCode(studentId: string): Observable<KeyValueDTO> {
        return this.http.get<KeyValueDTO>(`${this.baseUrl}/special-code/${studentId}`);
    }

    generateStudentSpecialCode(studentId: string): Observable<KeyValueDTO> {
        return this.http.get<KeyValueDTO>(`${this.baseUrl}/special-code/generate/${studentId}`, {
            headers: logRequest(),
        });
    }

    addParentStudentSpecialCode(specialCode: string): Observable<Student> {
        return this.http.get<Student>(`${this.baseUrl}/special-code/${specialCode}/add-parent`, {
            headers: logRequest(),
        });
    }

    sendStudentSpecialCode(studentId: string, email: string): Observable<HttpResponse<void>> {
        return this.http.get<void>(`${this.baseUrl}/special-code/${studentId}/email/${email}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    deleteStudentFromParent(studentId: string): Observable<HttpResponse<void>> {
        return this.http.get<void>(`${this.baseUrl}/special-code/${studentId}/delete-student`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getStudentsEnrollmentInfo(schoolId: string): Observable<EnrollmentInfo> {
        return this.http.get<EnrollmentInfo>(`${this.baseUrl}/enrollment-info/${schoolId}`);
    }

    getTranscriptTypes(): TranscriptTypes[] {
        return [
            { type: TranscriptType.STUDENT_TRANSCRIPT_BY_ACADEMIC_YEAR, viewValue: 'NAV.STUDENT_TRANSCRIPT.TITLE' },
            {
                type: TranscriptType.STUDENT_TRANSCRIPT_BY_SUBJECT,
                viewValue: 'NAV.STUDENT_TRANSCRIPT.BY_SUBJECT.TITLE',
            },
        ];
    }

    getGPACalculationTypes(): GPACalculationTypes[] {
        return [
            {
                type: GPACalculationType.RECALCULATE_GPA,
                viewValue: 'NAV.STUDENT_TRANSCRIPT.GPA_CALCULATION_TYPE.RECALCULATE_GPA',
            },
            {
                type: GPACalculationType.USE_PRECALCULATED_GPA,
                viewValue: 'NAV.STUDENT_TRANSCRIPT.GPA_CALCULATION_TYPE.USE_PRECALCULATED_GPA',
            },
        ];
    }

    generateStudentId(dto: StudentIdGenerateDTO): Observable<HttpResponse<string>> {
        return this.http.post<string>(`${this.baseUrl}/generate-student-id`, dto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    printStudentProfile(studentId: string): Observable<HttpResponse<any>> {
        return this.http.get(`${this.baseUrl}/download/student-profile/${studentId}`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    searchStudentEnrollmentReport(searchDTO: StudentEnrollmentSearch): Observable<HttpResponse<StudentEnroll[]>> {
        searchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<StudentEnroll[]>(`${this.baseUrl}/search-student-enrollments`, searchDTO, {
            observe: 'response',
        });
    }

    getAlphabeticalStudentListByAcademicPeriodPageable(
        academicYearId: string,
        params?: Pageable
    ): Observable<HttpResponse<IPage<AlphabeticalStudentList>>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<IPage<AlphabeticalStudentList>>(
            `${this.baseUrl}/school/${schoolId}/${academicYearId}/alphabetical-order`,
            {
                observe: 'response',
                params: httpParams(params),
            }
        );
    }

    exportAlphabeticalStudentListByAcademicPeriod(academicYearId: string): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get(`${this.baseUrl}/school/${schoolId}/${academicYearId}/alphabetical-order/export`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    downloadStudentRegistrationBook(searchDTO: StudentEnrollmentSearch): Observable<HttpResponse<any>> {
        searchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post(`${this.baseUrl}/registration-book/doc`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getStudentInfoReport(
        academicYearId: string,
        params?: Pageable
    ): Observable<HttpResponse<IPage<StudentInfoReport>>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<IPage<StudentInfoReport>>(
            `${this.baseUrl}/school/${schoolId}/${academicYearId}/student-info-report`,
            {
                observe: 'response',
                params: httpParams(params),
            }
        );
    }

    exportStudentInfoReport(academicYearId: string): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get(`${this.baseUrl}/school/${schoolId}/${academicYearId}/student-info-report/export`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getStudentAchievements(
        academicYearId: string,
        params?: Pageable
    ): Observable<HttpResponse<IPage<BookOfTheAchievments>>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<IPage<BookOfTheAchievments>>(
            `${this.baseUrl}/school/${schoolId}/${academicYearId}/student-achievements`,
            {
                observe: 'response',
                params: httpParams(params),
            }
        );
    }

    exportStudentAchievements(academicYearId: string): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get(`${this.baseUrl}/school/${schoolId}/${academicYearId}/student-achievements/export`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getSocialPassportOfSchoolReport(): Observable<SocialPassportOfSchool> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<SocialPassportOfSchool>(`${this.baseUrl}/school/${schoolId}/social-passport-of-school`);
    }

    downloadSocialPassportOfSchoolReportt(): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get(`${this.baseUrl}/school/${schoolId}/social-passport-of-school/download`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    changeEnrollDate(
        studentId: string,
        courseId: string,
        academicYearId: string,
        enrollDate: Date
    ): Observable<HttpResponse<any>> {
        return this.http.post<any>(
            `${this.baseUrl}/change-course-enroll-date`,
            {
                studentId,
                academicYearId,
                courseId,
                enrollDate,
            },
            {
                observe: 'response',
                headers: logRequest(),
            }
        );
    }

    getStudentSiblings(studentId: string): Observable<IStudent[]> {
        return this.http.get<IStudent[]>(`${this.baseUrl}/${studentId}/siblings`);
    }

    getStudentSiblingsBySelection(studentIds: string[]): Observable<IStudent[]> {
        return this.http.post<IStudent[]>(`${this.baseUrl}/siblings-selection`, { studentIds: studentIds });
    }
    getStudentsForSelect(schoolId: string, academicPeriodId: string): Observable<IPageableStudent[]> {
        return this.http.get<IPageableStudent[]>(
            `${this.baseUrl}/school/${schoolId}/academic-period/${academicPeriodId}/students-for-select`
        );
    }

    assignCounselor(employeeId: string, studentIds: string[]) {
        return this.http.post<any>(`${this.baseUrl}/assign-counselor/${employeeId}`, studentIds);
    }

    changeCounselor(oldId: string, newId: string[]) {
        return this.http.get<any>(`${this.baseUrl}/change-counselor/${oldId}/${newId}`);
    }

    bulkAssignCounselor(employeeId: string, gradeLevels: string[]) {
        return this.http.post<any>(`${this.baseUrl}/bulk-counselor-assign/${employeeId}`, gradeLevels);
    }

    removeCounselor(studentIds: string[]) {
        return this.http.post<any>(`${this.baseUrl}/remove-counselor`, studentIds);
    }
}
